import FilterDropdownController from "./filter_dropdown_controller";

export default class extends FilterDropdownController {
  static targets = ["cityDropdown", "zipCodeDropdown", "cityFields", "zipCodeFields", "locationEdited"];
  static values = {
    cityStateMap: Object,
    zipCodeStateMap: Object,
    cityFieldName: String,
    zipCodeFieldName: String,
  };

  connect() {
    super.connect();
    this.updateSearchByVisibility();
  }

  markLocationEdited() {
    if (this.hasLocationEditedTarget) this.locationEditedTarget.value = "1";
  }

  get locationGroups() {
    return [
      {
        value: "city",
        dropdowns: this.cityDropdownTargets,
        fields: this.cityFieldsTargets,
        stateMap: this.cityStateMapValue,
        fieldName: this.cityFieldNameValue,
      },
      {
        value: "zip_code",
        dropdowns: this.zipCodeDropdownTargets,
        fields: this.zipCodeFieldsTargets,
        stateMap: this.zipCodeStateMapValue,
        fieldName: this.zipCodeFieldNameValue,
      },
    ];
  }

  updateLocationOptions(event) {
    const selectedState = event?.target?.value ?? this._visibleFilterInput("state")?.value;

    this.locationGroups.forEach((group) => this.replaceDropdownOptions(group, selectedState));
  }

  updateSearchByVisibility(event) {
    const selected = event?.target?.value ||
      this.element.querySelector("[data-location-search-by-option]:checked")?.value || "city";

    this.locationGroups.forEach((group) => {
      const visible = group.value === selected;

      this.toggleFields(group.fields, visible);
      if (event && !visible) this.clearDropdowns(group);
    });
  }

  replaceDropdownOptions(group, selectedState) {
    const options = this.optionsForState(group.stateMap, selectedState);
    let updated = false;

    group.dropdowns.forEach((element) => {
      const dropdown = this.multiSelectControllerFor(element);
      if (!dropdown) return;

      dropdown.replaceOptions(options);
      updated = true;
    });

    if (!updated && this.isFilterField(group.fieldName)) this.rebuildHidden(group.fieldName, []);
  }

  optionsForState(optionsMap, state) {
    if (state) return optionsMap[state] || [];

    const seen = new Set();
    return Object.values(optionsMap).flat().filter((option) => {
      if (seen.has(String(option.value))) return false;
      seen.add(String(option.value));
      return true;
    }).sort((left, right) => left.label.localeCompare(right.label));
  }

  toggleFields(targets, visible) {
    targets.forEach((element) => {
      element.classList.toggle("tw-hidden", !visible);
      element.setAttribute("aria-hidden", String(!visible));
    });
  }

  clearDropdowns(group) {
    group.dropdowns.forEach((element) => {
      this.multiSelectControllerFor(element)?.clearSelection({ emitChange: false });
    });
    if (this.isFilterField(group.fieldName)) this.clearMultiSelectHidden(group.fieldName);
  }

  groupState() {
    const defaultCount = [
      this.filterFieldValue("search_by") === "city",
      this.filterFieldValue("mode") === "exact",
    ].filter(Boolean).length;
    const count = Math.max(super.groupState().count - defaultCount, 0);

    return { count, applied: count > 0 };
  }

  submitForm() {
    this.element.querySelectorAll("input[type='hidden'][data-filter-default-value]").forEach((input) => {
      input.disabled = input.value === input.dataset.filterDefaultValue;
    });
    super.submitForm();
  }

  filterFieldValue(field) {
    return this.element.querySelector(`input[type="hidden"][data-filter-field="${field}"]`)?.value;
  }

  isFilterField(fieldName) {
    return fieldName?.startsWith("q[");
  }

  resetFilters() {
    this._resetFilterFields();
    this.locationGroups.forEach((group) => this.clearDropdowns(group));
    const mode = this._visibleFilterInput("mode");
    if (mode) {
      mode.value = "exact";
      mode.dispatchEvent(new Event("change", { bubbles: true }));
    }
    const citySearch = this.element.querySelector('[data-location-search-by-option][value="city"]');
    if (citySearch) citySearch.checked = true;
    this.updateSearchByVisibility({ target: citySearch || { value: "city" } });
    this.updateLocationOptions();
    this._syncFilterFields();
    this.updateFilterState();
    this.submitForm();
  }
}
